import { SnackbarProvider } from "notistack";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import React, { useEffect, useState } from "react";
import Typography from "@mui/material/Typography";

import {
  ProductionBannerMessage,
  HeaderActionBorder,
  HeaderArrowDropDownIcon,
  HeaderAvatarGridContainer,
  HeaderCustomAvatarGrid,
  HeaderHelpOutlineOutlinedIcon,
  HeaderToolbar,
  HeaderUserDetails,
  LogoImg,
  LogoLink,
} from "../../styles/styles";
import { colors } from "../../../styles/colors";
import {
  getConcatStringOrNull,
  getLocalStorageData,
  length,
  getValue
} from "../../../utils";
import { currentDate, formatDate } from "../../../utils/DateTime";
import { DateFormats } from "../../../constants/Date";
import { flexAlignCentre } from "../../../styles/mui/styles/display";
import { height100PCT } from "../../../styles/mui/styles/heights";
import {
  inputType,
  PriorAuthMasterApiKey,
} from "../../../constants/Constants";
import { msalInstance } from "../../..";
import { SnackbarUtilsConfigurator } from "../../../utils/SnackbarProvider";
import { tangoLogo } from "../../../constants/ImagePaths";
import { UserDetailsPayload } from "../../../models/User";
import FileUpload from "../../../components/formComponents/FileUpload";
import fontWeight from "../../../styles/mui/fontWeight";
import Menus from "../../containers/Menus";
import { DocumentTabType } from "../../../constants/Document";
import {
  apiCallInterval,
  isProductionEnvironment,
} from "../../../constants/Config";
import { UserModifiedResponse } from "../../../models/Api/User";
import { MasterSyncResponse } from "../../../models/Api/Master";
import { MasterSyncActionDispatchTypes } from "../../../constants/Master";
import { ApiValueToKey } from "../../../constants/MasterCache";
import { MasterSync } from "../../../models/Master";

export interface PropsFromState {
  user: UserDetailsPayload;
  userModified: UserModifiedResponse;
  masterSyncData: MasterSyncResponse;
}
export interface PropsFromDispatch {
  getLogin: () => void;
  putLoggedInUserDetails: (userId: string, payload: UserDetailsPayload) => void;
  getUserModified: () => void;
  getAllRoles: () => void;
  getUserPermission: (userId: number) => void;
  getUserModules: (userId: number) => void;
  getUserDetails: (id: number) => void;
  masterSync: (payload: MasterSync[]) => void;
  resetStates: (actionType: string[]) => void;
  masterApiCall: (actionType: string) => void;
}

type AllProps = PropsFromState & PropsFromDispatch;

const Header: React.FC<AllProps> = ({
  user,
  getLogin,
  getUserModified,
  userModified,
  getAllRoles,
  getUserPermission,
  getUserModules,
  masterSync,
  masterSyncData,
  resetStates,
  masterApiCall,
}) => {
  const [userDetails, setUserDetails] = useState<UserDetailsPayload>(user);
  const [files, setFiles] = useState<any>([]);
  const [userImage, setUserImage] = useState<any>([]);
  const account = msalInstance.getActiveAccount();
  const { loading: loadingMasterSync, response: masterSyncResponse } =
    masterSyncData;
  const isUserPermissionModified = getValue(userModified, "response.modifiedOn", null);

  useEffect(() => {
    getLogin();
  }, []);

  useEffect(() => {
    setUserDetails(user);
  }, [user]);

  useEffect(() => {
    setInterval(() => {
      getUserModified();
    }, apiCallInterval * 60 * 1000);
    setInterval(() => {
      const payload = getLocalStorageData(PriorAuthMasterApiKey);
      resetStates([MasterSyncActionDispatchTypes.MASTER_SYNC_RESET]);
      if (!loadingMasterSync) {
        masterSync(payload);
      }
    }, apiCallInterval * 60 * 1000);

  }, []);

  useEffect(() => {
    if (length(masterSyncResponse)) {
      masterSyncResponse.forEach((singleOption) => {
        if (singleOption.isSynced) {
          localStorage.removeItem(ApiValueToKey[singleOption.apiName]);
          masterApiCall(ApiValueToKey[singleOption.apiName]);
        }
      });
    }
  }, [masterSyncResponse]);

  useEffect(() => {
    if (getValue(userModified, "response.isUserModified") === true) {
      if (user && user.userId && user.userId !== -1) {
        getUserModules(user.userId);
        getUserPermission(user.userId);
      }
      getAllRoles();
    }
  }, [isUserPermissionModified])

  const updateUserDetails = (user: UserDetailsPayload) => {
    // TBD
    if (userDetails) {
      user.userId = userDetails.userId > 0 ? userDetails.userId : 0;
      user.userPhoto = userDetails.userPhoto;
      user.fullName = userDetails.fullName;
      user.userName = userDetails.userName;
      user.email = userDetails.email;
      user.userPhotoBlobId = userDetails.userPhotoBlobId;
      user.designation = userDetails.designation;
      user.gender = userDetails.gender;
      user.userAddress = userDetails.userAddress;
      user.lastLogin = currentDate();
      user.userStatus = userDetails.isSuspended ? "Suspended" : "Active";
      user.isSuspended = userDetails.isSuspended;
      user.userRoles = userDetails.userRoles;
      user.userCreated = getConcatStringOrNull(userDetails.userCreated);
      user.userPhotoBlobId = 1;
      user.objectId = "";
      user.dateCreated = formatDate(
        new Date().toString(),
        DateFormats.YYYY_MM_DD
      );
      user.phoneNo = userDetails.phoneNo;
    }
  };

  useEffect(() => {
    const user: UserDetailsPayload = {
      ...userDetails,
      dateCreated: "",
      userName: "",
      email: "",
      userStatus: "",
      userPhotoBlobId: 1,
      userId: 0,
      userCreated: null,
      firstName: "",
      lastName: "",
      lastLogin: "",
    };
    updateUserDetails(user);
  }, [userDetails]);

  return (
    <>
      <SnackbarProvider
        maxSnack={3}
        anchorOrigin={{ horizontal: "center", vertical: "bottom" }}
      >
        <SnackbarUtilsConfigurator />
        <Paper elevation={3}>
          <HeaderToolbar disableGutters>
            <Grid container sx={height100PCT} pl={3} pr={3}>
              <Grid size={4}>
                <Box sx={height100PCT}>
                  <Grid container sx={height100PCT}>
                    <Grid sx={flexAlignCentre} size={12}>
                      <LogoLink to="/">
                        <LogoImg alt="Pronet 2.0 Logo" src={tangoLogo} />
                      </LogoLink>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
              <Grid size={4} sx={ProductionBannerMessage}>
                {isProductionEnvironment !== 1 && <Typography
                  variant="h4"
                  fontWeight={fontWeight.Weight[6]}
                  color={colors.red[100]}
                >
                  NOT PRODUCTION
                </Typography>}
              </Grid>
              <Grid size={4}>
                <Grid
                  container
                  sx={HeaderAvatarGridContainer}
                  justifyContent="flex-end"
                >
                  <Grid sx={HeaderCustomAvatarGrid} mr={1}>
                    <FileUpload
                      disabled={true}
                      files={files}
                      inputType={inputType.userProfile}
                      patientImageProps={userImage}
                      setFiles={setFiles}
                      setPatientImage={setUserImage}
                      type={DocumentTabType.DOCUMENT}
                    />
                  </Grid>
                  <Grid>
                    <Grid
                      container
                      sx={HeaderUserDetails}
                      justifyContent={"flex-end"}
                    >
                      <Grid size={"auto"}>
                        <Typography variant="h6" color={colors.fonts[6]}>
                          Welcome
                        </Typography>
                      </Grid>
                      <Grid size={"auto"}>
                        <Typography
                          variant="h6"
                          color={colors.fonts[26]}
                          fontWeight={fontWeight.Weight[6]}
                        >
                          {account ? account.name : ""}
                        </Typography>
                      </Grid>
                      <Grid size={"auto"}>
                        <HeaderArrowDropDownIcon />
                      </Grid>
                      <Grid size={"auto"}>
                        <HeaderActionBorder />
                      </Grid>
                      <Grid size={"auto"}>
                        <HeaderHelpOutlineOutlinedIcon></HeaderHelpOutlineOutlinedIcon>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </HeaderToolbar>
          <Menus />
        </Paper>
      </SnackbarProvider>
    </>
  );
};

export default Header;
